import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Button,
  MenuItem,
  Select,
  SelectChangeEvent,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import SwitchToggle from "../../components/SwitchToggle";
import dayjs from "dayjs";
import { useGetCoupons, useDeleteCoupon, useUpdateCoupon } from "../../api/hooks/useCoupon";
import { showSuccess, showError } from "../../helpers/toast";
import { VALUE_TYPE, DISCOUNT_OPTION } from "../../constants/discount";
import type { Coupon } from "../../api/types/coupon.types";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import Pagination from "../../components/Panigation";

const DiscountCoupon = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [couponToDelete, setCouponToDelete] = useState<string | null>(null);

  // Debounce search term
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1); // Reset to page 1 when search changes
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  // Fetch coupons
  const { data: couponsData, isLoading: isLoadingCoupons, refetch: refetchCoupons, error: couponsError } = useGetCoupons(
    {
      ...(debouncedSearchTerm.trim() && { search: debouncedSearchTerm.trim() }),
      ...(statusFilter !== "all" && { status: statusFilter === "active" }),
      page,
      limit,
      offset: (page - 1) * limit,
    },
    { enabled: true }
  );

  // Extract coupons and totalCount from response
  const { coupons, totalCount } = useMemo(() => {
    const data = couponsData?.data;
    let couponsList: Coupon[] = [];
    let total = 0;

    if (Array.isArray(data)) {
      couponsList = data;
      total = data.length;
    } else if (data && typeof data === 'object') {
      const dataObj = data as any;
      if ('list' in dataObj && Array.isArray(dataObj.list)) {
        couponsList = dataObj.list;
        total = dataObj.totalCount || dataObj.list.length;
      } else if ('data' in dataObj && Array.isArray(dataObj.data)) {
        couponsList = dataObj.data;
        total = dataObj.totalCount || dataObj.data.length;
      } else if (dataObj.totalCount !== undefined) {
        total = dataObj.totalCount;
      }
    }

    if (!Array.isArray(couponsList)) {
      couponsList = [];
    }

    return {
      coupons: couponsList,
      totalCount: total || couponsList.length,
    };
  }, [couponsData]);

  // Delete coupon mutation
  const { mutate: deleteCoupon, isPending: isDeleting } = useDeleteCoupon({
    onSuccess: (response) => {
      showSuccess(response.message || "Coupon deleted successfully");
      refetchCoupons();
      setDeleteDialogOpen(false);
      setCouponToDelete(null);
    },
    onError: (error: any) => {
      showError(error?.response?.data?.message || "Failed to delete coupon");
    },
  });

  // Update coupon status mutation
  const { mutate: updateCouponStatus, isPending: isUpdatingStatus } = useUpdateCoupon({
    onSuccess: (response) => {
      showSuccess(response.message || "Coupon status updated successfully");
      refetchCoupons();
    },
    onError: (error: any) => {
      showError(error?.response?.data?.message || "Failed to update coupon status");
    },
  });

  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  // Get status based on expiry date
  const getStatus = (expiryDate?: string) => {
    if (!expiryDate) {
      return { label: "Active", color: "#00B67A" };
    }
    const today = dayjs();
    const expiry = dayjs(expiryDate);

    if (today.isAfter(expiry)) {
      return { label: "Expired", color: "#D32F2F" };
    }
    return { label: "Active", color: "#00B67A" };
  };

  // Handle status toggle
  const handleStatusToggle = (coupon: Coupon) => (event: React.ChangeEvent<HTMLInputElement>) => {
    updateCouponStatus({
      id: coupon._id,
      data: {
        name: coupon.name,
        value: coupon.value,
        valueType: coupon.valueType,
        usageLimit: coupon.usageLimit,
        expiryDate: coupon.expiryDate || "",
        option: coupon.option,
        newUserOnly: coupon.newUserOnly,
        status: event.target.checked,
      },
    });
  };

  // Handle delete coupon
  const handleDeleteCoupon = (id: string) => {
    setCouponToDelete(id);
    setDeleteDialogOpen(true);
  };

  // Confirm delete coupon
  const handleConfirmDelete = () => {
    if (couponToDelete) {
      deleteCoupon(couponToDelete);
    }
  };

  // Cancel delete coupon
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setCouponToDelete(null);
  };

  // Handle view coupon
  const handleViewCoupon = (coupon: Coupon) => {
    navigate("/discounts/view", { state: { couponId: coupon._id } });
  };

  // Handle edit coupon
  const handleEditCoupon = (coupon: Coupon) => {
    navigate("/discounts/add", { state: { couponId: coupon._id } });
  };

  const totalPages = Math.ceil(totalCount / limit);
  const onPageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <>
      <div className="main_title">
        <h1>Discount & Coupon Management</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
          Discount & Coupon Management
        </p>
      </div>

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              onCross={() => setSearchTerm("")}
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setSearchTerm}
              placeholder="Search coupons..."
            />
          </div>

          <div className="right_s" style={{ display: "flex", gap: "12px" }}>
            {/* FILTER */}
            <div className="form">
              <div className="control_group">
                <Select
                  value={statusFilter}
                  onChange={(e: SelectChangeEvent) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  size="small"
                  sx={{
                    width: "150px",
                    background: "#fff",
                    borderRadius: "6px",
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </div>
            </div>

            {/* ADD BUTTON */}
            <Button
              className="btn btn_primary"
              onClick={() => navigate("/discounts/add")}
              disabled={isLoadingCoupons || isDeleting || isUpdatingStatus}
            >
              Add Discount and Coupon
            </Button>
          </div>
        </div>

        <TableContainer className="table_container">
          {isLoadingCoupons ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
              <CircularProgress />
            </Box>
          ) : couponsError ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
              <p>Error loading coupons. Please try again.</p>
            </Box>
          ) : coupons.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
              <p>No coupons found.</p>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Sr. No</TableCell>
                  <TableCell>Offer/Discount Name</TableCell>
                  <TableCell>Coupon Code</TableCell>
                  <TableCell>Discount</TableCell>
                  <TableCell>Discount Option</TableCell>
                  <TableCell>Usage Limit</TableCell>
                  <TableCell>Expiry Date</TableCell>
                  <TableCell>New User Only</TableCell>
                  <TableCell>Created Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {coupons.map((coupon: Coupon, index: number) => {
                  const status = getStatus(coupon.expiryDate);
                  const discountDisplay = coupon.valueType === VALUE_TYPE.FLAT
                    ? `$${coupon.value}`
                    : `${coupon.value}%`;

                  return (
                    <TableRow hover key={coupon._id}>
                      <TableCell>{(page - 1) * limit + index + 1}</TableCell>
                      <TableCell>{coupon.name}</TableCell>

                      <TableCell>
                        {coupon.code ? (
                          <Button
                            variant="outlined"
                            size="small"
                            sx={{
                              borderColor: "#00B67A",
                              color: "#00B67A",
                              fontWeight: 600,
                              borderRadius: "6px",
                              px: 1.5,
                              py: 0.2,
                            }}
                          >
                            {coupon.code}
                          </Button>
                        ) : (
                          coupon.couponCode ? coupon.couponCode : "N/A"
                        )}
                      </TableCell>

                      <TableCell>{discountDisplay}</TableCell>
                      <TableCell>
                        {coupon.option === DISCOUNT_OPTION.ONE_TIME ? "One Time" : "Recurring"}
                      </TableCell>
                      <TableCell>{coupon.usageLimit}</TableCell>
                      <TableCell>{coupon.expiryDate ? formatDate(coupon.expiryDate) : "No expiry"}</TableCell>
                      <TableCell>
                        {coupon.newUserOnly ? (
                          <span className="status_btn c_success">Yes</span>
                        ) : (
                          <span className="status_btn c_warning">No</span>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(coupon.createdAt)}</TableCell>

                      {/* STATUS COLUMN */}
                      <TableCell>
                        <SwitchToggle
                          checked={coupon.status ?? true}
                          onChange={handleStatusToggle(coupon)}
                          disabled={isLoadingCoupons || isDeleting || isUpdatingStatus}
                        />
                      </TableCell>

                      <TableCell>
                        <div className="table_actions v2">
                          <IconButton
                            onClick={() => handleViewCoupon(coupon)}
                            disabled={isLoadingCoupons || isDeleting || isUpdatingStatus}
                            title="View Coupon"
                          >
                            <img
                              src="/static/images/eye_icon.svg"
                              alt="View"
                            />
                          </IconButton>

                          <IconButton
                            onClick={() => handleEditCoupon(coupon)}
                            disabled={isLoadingCoupons || isDeleting || isUpdatingStatus}
                            title="Edit Coupon"
                          >
                            <img
                              src="/static/images/edit2_icon.svg"
                              alt="Edit"
                            />
                          </IconButton>

                          <IconButton
                            onClick={() => handleDeleteCoupon(coupon._id)}
                            disabled={isLoadingCoupons || isDeleting || isUpdatingStatus}
                            title="Delete Coupon"
                          >
                            <img
                              src="/static/images/trash2_icon.svg"
                              alt="Delete"
                            />
                          </IconButton>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {/* Pagination */}
        {!isLoadingCoupons && !couponsError && totalCount > 0 && (
          <Pagination
            module={coupons}
            page={page}
            onPageChange={onPageChange}
            totalPages={totalPages}
            totalCount={totalCount}
            setPage={setPage}
            limit={limit}
          />
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Coupon"
        message="Are you sure you want to delete this coupon? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonColor="error"
        isLoading={isDeleting}
        loadingText="Deleting..."
      />
    </>
  );
};

export default DiscountCoupon;
